import { create } from "zustand";
import {
  BOARD_SIZE,
  SIM_VERSION,
  type AttackPlan,
  TICKS_PER_SECOND,
  emptyLayout,
  traceBattle,
  type Frame,
  type Layout,
} from "@empire/core-sim";
import { toLayout, type ReplayResponse, type WireFrame } from "@empire/shared";
import { fetchReplay, reportDesync } from "../api/attack.js";
import { guessedLayout } from "./intel.js";
import { useSessionStore } from "./session.js";

/**
 * Watching a battle back (S4-1), and checking it really is the battle (S4-4).
 *
 * There are two kinds of replay and they arrive differently (D-096). The
 * defender — and an attacker who broke through — gets the inputs and re-runs
 * the simulation here, which is the only version that can be VERIFIED: running
 * it produces a fingerprint, and if that fingerprint disagrees with the
 * server's then one of us is wrong and the replay must not be shown. A fogged
 * attacker gets a recorded stream instead, with no layout to re-run, so there
 * is nothing to verify and nothing to claim.
 */

export const SPEEDS = [1, 2, 4] as const;
export type Speed = (typeof SPEEDS)[number];

const MS_PER_TICK = 1000 / TICKS_PER_SECOND;

export type ReplayStatus = "idle" | "loading" | "ready" | "error" | "desync";

interface ReplayState {
  battleId: string | null;
  status: ReplayStatus;
  error: string | null;

  replay: ReplayResponse | null;
  /** The board to draw under the units. Fogged tiles are empty ground. */
  layout: Layout;
  /** Row-major indices this viewer never saw. Hatched on the board. */
  fog: number[];
  frames: Frame[];
  /** True when we re-ran it ourselves and the fingerprint matched. */
  verified: boolean;

  playing: boolean;
  /**
   * What the viewer has tapped while paused (S4-5), or null.
   *
   * Only ever set while paused. Inspecting a moving unit would be a popover
   * whose numbers change under the reader's eyes, and the reason to tap
   * something is that you have stopped to look at it.
   */
  inspecting: { unitId: number | null; tile: number | null } | null;
  speed: Speed;
  /** Fractional frame index, so playback is smooth at any speed. */
  position: number;

  /** Tap something while paused. Pass nulls to dismiss. */
  inspect: (unitId: number | null, tile: number | null) => void;

  /**
   * Whose battle this is, for the words on screen. "Held" is a defender's
   * word: shown to an attacker whose army just died, it reads like a win.
   */
  role: "attacker" | "defender" | null;
  open: (battleId: string, role?: "attacker" | "defender") => Promise<void>;
  close: () => void;
  togglePlay: () => void;
  setSpeed: (speed: Speed) => void;
  seek: (frameIndex: number) => void;
  skipToEnd: () => void;
  /** Called from the render loop. Returns the frame to draw, or null. */
  advance: (deltaMs: number) => Frame | null;
}

export const useReplayStore = create<ReplayState>((set, get) => ({
  battleId: null,
  status: "idle",
  error: null,
  replay: null,
  layout: emptyLayout(),
  fog: [],
  frames: [],
  verified: false,
  playing: false,
  inspecting: null,
  speed: 1,
  position: 0,
  role: null,

  open: async (battleId, role) => {
    const token = await useSessionStore.getState().token();
    if (!token) return;

    set({
      battleId,
      role: role ?? null,
      status: "loading",
      error: null,
      replay: null,
      frames: [],
      fog: [],
      verified: false,
      position: 0,
      playing: false,
      inspecting: null,
    });

    let replay: ReplayResponse;
    try {
      replay = await fetchReplay(token, battleId);
    } catch {
      set({ status: "error", error: "Couldn't load that battle." });
      return;
    }

    // The version the battle was fought under is not necessarily the version
    // this app bundles. Re-running it would produce a different battle, so we
    // say so instead of showing one.
    if (replay.simVersion !== SIM_VERSION) {
      set({
        status: "error",
        replay,
        error: `This battle was fought under rules version ${replay.simVersion}; this app runs ${SIM_VERSION}. Update the app to watch it.`,
      });
      return;
    }

    if (replay.layout) {
      const layout = toLayout(replay.layout);
      const { frames, hash } = rerun(layout, replay);

      // The check the whole determinism promise rests on. A mismatch means the
      // server's battle and ours are different battles — so we show neither,
      // and tell the server, because it is the only way anybody finds out.
      if (hash !== replay.resultHash) {
        void reportDesync(token, battleId, {
          clientHash: hash,
          platform: "web",
          simVersion: SIM_VERSION,
        }).catch(() => undefined);
        set({
          status: "desync",
          replay,
          error:
            "This replay does not match what the server recorded, so it is not being shown. The result above still stands — the report has been sent.",
        });
        return;
      }

      set({
        status: "ready",
        replay,
        layout,
        fog: [],
        frames,
        verified: true,
        playing: true,
      });
      return;
    }

    // Fogged: no layout to re-run, so play the stream the server recorded.
    const layout = guessedLayout(replay.view);
    set({
      status: "ready",
      replay,
      layout,
      fog: fogIndices(replay),
      frames: replay.frames ?? [],
      verified: false,
      playing: true,
    });
  },

  close: () =>
    set({
      battleId: null,
      status: "idle",
      replay: null,
      frames: [],
      playing: false,
      position: 0,
      inspecting: null,
    }),

  inspect: (unitId, tile) => {
    if (get().playing) return;
    set({ inspecting: unitId === null && tile === null ? null : { unitId, tile } });
  },

  togglePlay: () => {
    const { playing, position, frames } = get();
    // Playing again dismisses the popover: it described one frozen moment, and
    // that moment is over.
    set({ inspecting: null });
    // Pressing play at the end starts again rather than doing nothing.
    if (!playing && position >= frames.length - 1) set({ position: 0, playing: true });
    else set({ playing: !playing });
  },

  setSpeed: (speed) => set({ speed }),

  seek: (frameIndex) => {
    const { frames } = get();
    const clamped = Math.max(0, Math.min(frameIndex, Math.max(frames.length - 1, 0)));
    // Scrubbing keeps whatever is being inspected, because "what had this tower
    // done by now" is exactly the question a scrubber is for.
    set({ position: clamped, playing: false });
  },

  skipToEnd: () => {
    const { frames } = get();
    set({ position: Math.max(frames.length - 1, 0), playing: false });
  },

  advance: (deltaMs) => {
    const { frames, playing, speed, position } = get();
    if (frames.length === 0) return null;
    if (!playing) return frames[Math.floor(position)] ?? null;

    const next = position + (deltaMs * speed) / MS_PER_TICK;
    if (next >= frames.length - 1) {
      // Hold on the last frame rather than snapping to an empty board: the
      // final moment is the one worth looking at.
      set({ position: Math.max(frames.length - 1, 0), playing: false });
      return frames[frames.length - 1] ?? null;
    }
    set({ position: next });
    return frames[Math.floor(next)] ?? null;
  },
}));

/**
 * Re-run the battle from its inputs, collecting positions and the fingerprint.
 *
 * This is the SAME `simulate` the server ran, through the SAME tracer — so a
 * defender watching their own battle and an attacker watching the server's
 * recording of it are looking at one piece of code's opinion, not two. If it
 * were a renderer replaying a recording, a disagreement would be invisible.
 */
export function rerun(
  layout: Layout,
  replay: { readonly plan: AttackPlan }
): { frames: Frame[]; hash: string } {
  const { result, frames } = traceBattle({ simVersion: SIM_VERSION, layout, plan: replay.plan });
  return { frames: [...frames], hash: result.hash };
}

/** Which board squares this viewer never saw. */
export function fogIndices(replay: Pick<ReplayResponse, "view">): number[] {
  const unknown: number[] = [];
  for (let i = 0; i < replay.view.tiles.length && i < BOARD_SIZE * BOARD_SIZE; i++) {
    if (replay.view.tiles[i] === null) unknown.push(i);
  }
  return unknown;
}

/** Seconds into the battle, for the scrubber's label. */
export const secondsAt = (frames: readonly Frame[], position: number): number =>
  (frames[Math.floor(position)]?.tick ?? 0) / TICKS_PER_SECOND;

export type { Frame };
export type { WireFrame };

/** The headline for a battle, from the viewer's side of it. */
export function verdictWord(breach: boolean, role: "attacker" | "defender" | null): string {
  if (role === "attacker") return breach ? "Victory — you broke through" : "Defeat — your attack was stopped";
  if (role === "defender") return breach ? "Breached — they got in" : "Held — nobody got through";
  return breach ? "Breached" : "Held";
}
